import i18n from '@/i18n';
import {
  BadgeCheck, BarChart3, Boxes, Briefcase, Building2, FileBarChart, Flame, Gauge, Gavel, HardHat,
  Landmark, Leaf, LineChart, PlugZap, Receipt, Share2, ShieldCheck, Sun, TrendingUp,
  Waypoints, Wrench, type LucideIcon,
} from 'lucide-react';

/**
 * Зміст розділу «Документи» — чотири вкладки реєстру.
 *
 * ════════════════════════════════════════════════════════════════════════
 *  ЯК ДОДАТИ ДОКУМЕНТ
 *
 *  1. Покласти файл у `public/docs/` — назва латиницею, без пробілів
 *     (кирилиця в URL кодується й ламається в логах і при копіюванні).
 *  2. Дописати ОДИН рядок у потрібну групу нижче:
 *
 *       { get title() { return i18n.t('ak_docsdata_electricity_purchase_and_sale'); },
 *         date: '2025-03-12',
 *         file: '/docs/luchky-tepla-purchase-2025.pdf',
 *         station: 'лучки' },
 *
 *  3. Перезібрати стенд.
 *
 *  Більше нічого. Рік для фільтра рахується з `date` сам, дата показується
 *  в реквізиті сама, кнопка року з'являється сама — усі списки будуються з
 *  цих даних, нічого не зашито окремо.
 *
 *  `station` — підрядок назви активу в нижньому регістрі («лучки»). Без
 *  нього документ вважається спільним для всіх СЕС.
 * ════════════════════════════════════════════════════════════════════════
 *
 * Дані лежать окремо від верстки: перелік живий, його правитимуть частіше за
 * оформлення, і шукати рядок серед JSX незручно.
 *
 * Заголовок і реквізит розділені (`title` / `meta`): у документі вони йшли
 * одним рядком через тире («Правила ринку - Постанова НКРЕКП від 14.03.2018
 * №307»), але це різні за вагою речі. Назву читають, номер постанови звіряють.
 */

/** Рік із дати — для фільтра. Приймає '2026-08-26' і '26.08.2026'. */
export const docYear = (date?: string): number | undefined => {
  if (!date) return undefined;
  const iso = /^(\d{4})-\d{2}-\d{2}$/.exec(date);
  if (iso) return Number(iso[1]);
  const ua = /^\d{2}\.\d{2}\.(\d{4})$/.exec(date);
  return ua ? Number(ua[1]) : undefined;
};

/** Дата у вигляді, звичному в документообігу: 26.08.2026. */
export const docDateLabel = (date?: string): string | undefined => {
  if (!date) return undefined;
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  return iso ? `${iso[3]}.${iso[2]}.${iso[1]}` : date;
};

export interface DocLink {
  title: string;
  /** Реквізит: ким і коли затверджено, номер. */
  meta?: string;
  url?: string;
  /**
   * Дата документа — ЄДИНЕ джерело і для фільтра за роком, і для реквізиту.
   * Формат '2026-08-26' або '26.08.2026'.
   *
   * Раніше тут були два поля — `year` для фільтра й дата текстом усередині
   * `meta`. Це означало писати те саме двічі й ризикувати розходженням.
   *
   * ЗВІДКИ ВЗЯТІ НАЯВНІ ДАТИ. У наданому переліку їх не було; видобуто з
   * самих PDF (розпакування потоків FlateDecode й пошук дат — poppler на
   * сервері немає). У повідомленнях Укренерго знайдено по три дати, і взята
   * саме дата документа: 14.03.2018 — це посилання на постанову НКРЕКП № 307,
   * а 28.10.2027 — кінець строку дії, тож обидві відкинуті.
   */
  date?: string;
  /**
   * Локальний файл документа в public/docs.
   *
   * Замовник надав сім файлів по СЕС «Еліос Лучки»: чотири повідомлення НЕК
   * «Укренерго», договір купівлі-продажу з «Тепла», договір сервісного
   * обслуговування з «Геліос Стратегія» і перелік ТКО. Решта позицій реєстру
   * файлів досі не має.
   */
  file?: string;
  /**
   * СЕС, якій належить файл (підрядок назви активу в нижньому регістрі).
   *
   * Без цього поля документи Лучок показувалися б під будь-якою обраною
   * станцією — тобто селектор СЕС брехав би. Коли обрана інша станція, запис
   * лишається в переліку, але БЕЗ файла: позиція в номенклатурі є, документа
   * для цього об'єкта немає.
   */
  station?: string;
}

/**
 * Прив'язка групи документів.
 *
 * 'station' — документ належить конкретному об'єкту: приєднання, розподіл,
 *   технічний паспорт, охорона, обслуговування. У кожної СЕС він свій.
 * 'company' — документ юрособи цілком: участь у балансуючій групі, право на
 *   Helios EMS, звітність до НКРЕКП і Держстату. Один на весь портфель.
 *
 * Потрібно для селектора СЕС у шапці: перемикання станції не має вдавати, що
 * змінює те, що від станції не залежить. Групи 'company' показуються завжди й
 * позначені чіпом, щоб було видно ЧОМУ вони не змінились.
 */
export type DocScope = 'station' | 'company';

export interface DocGroup {
  /**
   * Знак групи — монохромна іконка за її СЕНСОМ: приєднання до мереж — вилка,
   * облік — лічильник, зелений тариф — листок, ОСП — вузли мережі. Замінила
   * зелену риску, яка нічого не розрізняла: однакова біля всіх заголовків,
   * вона лише позначала початок групи, тоді як іконка ще й каже, про що група.
   */
  Icon: LucideIcon;
  label: string;
  scope: DocScope;
  items: DocLink[];
  /** Група документів, яких ще немає — показуємо приглушено. */
  pending?: boolean;
}

// ── 1. Нормативні акти ──────────────────────────────────────────────────────
export const LEGAL: DocGroup[] = [
  {
    get label() { return i18n.t('ak_docsdata_legislation'); }, Icon: Landmark, scope: 'company',
    items: [
      { get title() { return i18n.t('ak_docsdata_on_the_electricity_market'); }, get meta() { return i18n.t('ak_docsdata_law_of_ukraine'); }, url: 'https://zakon.rada.gov.ua/laws/show/2019-19/page#Text' },
      { get title() { return i18n.t('ak_docsdata_on_alternative_energy_sources'); }, get meta() { return i18n.t('ak_docsdata_law_of_ukraine'); }, url: 'https://zakon.rada.gov.ua/rada/show/555-15#Text' },
      { get title() { return i18n.t('ak_docsdata_on_amendments_to_improve_the_support_c'); }, get meta() { return i18n.t('ak_docsdata_law_of_ukraine_no_810_ix'); }, url: 'https://zakon.rada.gov.ua/laws/show/810-IX#Text' },
      { get title() { return i18n.t('ak_docsdata_on_prevention_of_abuse_in_wholesale_en'); }, get meta() { return i18n.t('ak_docsdata_law_of_ukraine_no_3141_ix'); }, url: 'https://zakon.rada.gov.ua/laws/show/3141-IX#Text' },
      { get title() { return i18n.t('ak_docsdata_on_the_recovery_and_green_transformati'); }, get meta() { return i18n.t('ak_docsdata_law_of_ukraine_no_3220_ix'); }, url: 'https://zakon.rada.gov.ua/laws/show/3220-IX#Text' },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_licensing'); }, Icon: BadgeCheck, scope: 'company',
    items: [
      { get title() { return i18n.t('ak_docsdata_licensing_conditions_for_conducting_bu'); }, get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_27_12_2017_no_1467'); }, url: 'https://zakon.rada.gov.ua/laws/show/v1467874-17#Text' },
      { get title() { return i18n.t('ak_docsdata_licensing_conditions_for_conducting_bu_2'); }, get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_22_07_2022_no_798'); }, url: 'https://zakon.rada.gov.ua/rada/show/v0798874-22#Text' },
      { get title() { return i18n.t('ak_docsdata_procedure_for_registration_of_wholesal'); }, get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_04_10_2023_no_1812'); }, url: 'https://zakon.rada.gov.ua/rada/show/v1812874-23#Text' },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_grid_connection'); }, Icon: PlugZap, scope: 'company',
    items: [
      { get title() { return i18n.t('ak_docsdata_transmission_system_code'); }, get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_14_03_2018_no_309'); }, url: 'https://zakon.rada.gov.ua/laws/show/v0309874-18#Text' },
      { get title() { return i18n.t('ak_docsdata_distribution_system_code'); }, get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_14_03_2018_no_310'); }, url: 'https://zakon.rada.gov.ua/laws/show/v0310874-18#Text' },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_metering'); }, Icon: Gauge, scope: 'company',
    items: [
      { get title() { return i18n.t('ak_docsdata_electricity_commercial_metering_code'); }, get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_14_03_2018_no_311'); }, url: 'https://zakon.rada.gov.ua/laws/show/v0311874-18#Text' },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_market'); }, Icon: LineChart, scope: 'company',
    items: [
      { get title() { return i18n.t('ak_docsdata_market_rules'); }, get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_14_03_2018_no_307'); }, url: 'https://zakon.rada.gov.ua/laws/show/v0307874-18#Text' },
      { get title() { return i18n.t('ak_docsdata_rules_of_the_day_ahead_and_intraday_ma'); }, get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_14_03_2018_no_308'); }, url: 'https://zakon.rada.gov.ua/laws/show/v0308874-18#Text' },
      { get title() { return i18n.t('ak_docsdata_retail_electricity_market_rules'); }, get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_14_03_2018_no_312'); }, url: 'https://zakon.rada.gov.ua/laws/show/v0312874-18#Text' },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_reporting'); }, Icon: FileBarChart, scope: 'company',
    items: [
      { get title() { return i18n.t('ak_docsdata_monitoring_reporting_forms_for_market'); }, get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_29_03_2019_no_450'); }, url: 'https://zakon.rada.gov.ua/laws/show/v0450874-19#Text' },
      { get title() { return i18n.t('ak_docsdata_neurc_reporting_forms_for_market_parti'); }, get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_28_02_2019_no_282'); }, url: 'https://zakon.rada.gov.ua/laws/show/v0282874-19#Text' },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_green_tariff'); }, Icon: Leaf, scope: 'company',
    items: [
      { get title() { return i18n.t('ak_docsdata_procedure_for_purchase_by_the_guarante'); }, get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_26_04_2019_no_641'); }, url: 'https://zakon.rada.gov.ua/laws/show/v0641874-19#Text' },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_operation'); }, Icon: Wrench, scope: 'company',
    items: [
      { get title() { return i18n.t('ak_docsdata_rules_for_safe_operation_of_consumers'); }, get meta() { return i18n.t('ak_docsdata_order_of_the_state_committee_for_occup'); }, url: 'https://zakon.rada.gov.ua/laws/show/z0093-98#Text' },
      { get title() { return i18n.t('ak_docsdata_rules_for_technical_operation_of_consu'); }, get meta() { return i18n.t('ak_docsdata_order_of_the_ministry_of_fuel_and_ener'); }, url: 'https://zakon.rada.gov.ua/laws/show/z1143-06/paran22#n22' },
      { get title() { return i18n.t('ak_docsdata_procedure_for_investigation_and_record'); }, get meta() { return i18n.t('ak_docsdata_resolution_of_the_cabinet_of_ministers'); }, url: 'https://zakon.rada.gov.ua/laws/show/337-2019-п#Text' },
      { get title() { return i18n.t('ak_docsdata_instruction_on_recording_and_investiga'); }, get meta() { return i18n.t('ak_docsdata_order_of_the_ministry_of_fuel_and_ener_2'); }, url: 'https://zakon.rada.gov.ua/laws/show/z0993-06#Text' },
    ],
  },
];

// ── 2. Договори (у розрізі контрагентів) ────────────────────────────────────
export const CONTRACTS: DocGroup[] = [
  {
    get label() { return i18n.t('ak_docsdata_npc_ukrenergo_tso'); }, Icon: Waypoints, scope: 'station',
    items: [
      { get title() { return i18n.t('ak_docsdata_on_the_provision_of_electricity_transm'); }, get meta() { return i18n.t('ak_docsdata_npc_ukrenergo_notification'); }, date: '2026-08-26', file: '/docs/luchky-ukrenergo-transmission.pdf', station: 'лучки' },
      { get title() { return i18n.t('ak_docsdata_on_the_provision_of_dispatch_operation'); }, get meta() { return i18n.t('ak_docsdata_npc_ukrenergo_notification'); }, date: '2026-08-26', file: '/docs/luchky-ukrenergo-dispatch.pdf', station: 'лучки' },
      { get title() { return i18n.t('ak_docsdata_on_the_settlement_of_electricity_imbal'); }, get meta() { return i18n.t('ak_docsdata_npc_ukrenergo_notification'); }, date: '2026-08-26', file: '/docs/luchky-ukrenergo-imbalance.pdf', station: 'лучки' },
      { get title() { return i18n.t('ak_docsdata_on_participation_in_the_balancing_mark'); }, get meta() { return i18n.t('ak_docsdata_npc_ukrenergo_notification'); }, date: '2026-08-28', file: '/docs/luchky-ukrenergo-balancing.pdf', station: 'лучки' },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_zakarpattiaoblenergo_pjsc_dso'); }, Icon: Share2, scope: 'station',
    items: [
      { get title() { return i18n.t('ak_docsdata_on_non_standard_connection'); } },
      { get title() { return i18n.t('ak_docsdata_consumer_on_the_provision_of_electrici'); } },
      { get title() { return i18n.t('ak_docsdata_on_the_provision_of_services_for_ensur'); } },
      { get title() { return i18n.t('ak_docsdata_with_a_producer_on_the_provision_of_el'); } },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_helios_aggregator_llc'); }, Icon: Sun, scope: 'company',
    items: [
      { get title() { return i18n.t('ak_docsdata_on_participation_in_a_balancing_group'); } },
      { get title() { return i18n.t('ak_docsdata_on_granting_the_right_to_use_the_helio'); } },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_tepla_llc'); }, Icon: TrendingUp, scope: 'company',
    items: [
      { get title() { return i18n.t('ak_docsdata_electricity_purchase_and_sale'); }, date: '2026-08-04', file: '/docs/luchky-tepla-purchase.pdf', station: 'лучки' },
      { get title() { return i18n.t('ak_docsdata_electricity_purchase_own_needs'); }, get meta() { return i18n.t('ak_docsdata_energy_supplier_or_tepla'); } },
    ],
  },
  // Перелік стандартизованих договорів v2.2 (22.10.2026): позиції «Ні (потрібно
  // додати)», розкладені за контрагентом із колонки «З ким укладається договір».
  {
    get label() { return i18n.t('ak_docsdata_market_operator_ueex'); }, Icon: Building2, scope: 'company',
    items: [{ get title() { return i18n.t('ak_docsdata_apd_services'); }, date: '2026-09-15', file: '/docs/luchky-apd-application-a860fb3439.pdf', station: 'лучки' }],
  },
  {
    get label() { return i18n.t('ak_docsdata_helios_strategia_llc'); }, Icon: Wrench, scope: 'station',
    items: [{ get title() { return i18n.t('ak_docsdata_on_the_provision_of_technical_maintena'); }, get meta() { return i18n.t('ak_docsdata_scan_without_a_text_layer_date_taken_f'); }, date: '2026-07-16', file: '/docs/luchky-helios-strategia-service.pdf', station: 'лучки' }],
  },
  {
    get label() { return i18n.t('ak_docsdata_contractors_sole_traders'); }, Icon: HardHat, scope: 'station',
    items: [
      { get title() { return i18n.t('ak_docsdata_relay_protection_setup'); } },
      { get title() { return i18n.t('ak_docsdata_land_lease'); } },
      { get title() { return i18n.t('ak_docsdata_on_insurance_of_pv_plant_bess_property'); }, date: '2026-07-24', file: '/docs/luchky-insurance-a860fb3439.pdf', station: 'лучки' },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_legion_alfa_security_agency_llc'); }, Icon: ShieldCheck, scope: 'station',
    items: [{ get title() { return i18n.t('ak_docsdata_for_stationary_physical_protection_of'); }, date: '2025-11-01', file: '/docs/luchky-security-a860fb3439.pdf', station: 'лучки' }],
  },
  {
    get label() { return i18n.t('ak_docsdata_asek_llc'); }, Icon: Flame, scope: 'station',
    items: [{ get title() { return i18n.t('ak_docsdata_for_maintenance_of_the_fire_alarm_syst'); } }],
  },
  {
    get label() { return i18n.t('ak_docsdata_company_services'); }, Icon: Briefcase, scope: 'company',
    items: [
      { get title() { return i18n.t('ak_docsdata_edo_services'); }, get meta() { return i18n.t('ak_docsdata_vchasno_other'); } },
      { get title() { return i18n.t('ak_docsdata_qes_certification'); }, get meta() { return i18n.t('ak_docsdata_csk_other'); } },
      { get title() { return i18n.t('ak_docsdata_audit_services'); }, get meta() { return i18n.t('ak_docsdata_audit_firm'); }, date: '2026-05-26', file: '/docs/luchky-audit-a860fb3439.pdf', station: 'лучки' },
      { get title() { return i18n.t('ak_docsdata_banking_services'); }, get meta() { return i18n.t('ak_docsdata_bank'); } },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_other'); }, Icon: Boxes, scope: 'station',
    pending: true,
    items: [
      { get title() { return i18n.t('ak_docsdata_on_the_provision_of_communication_it_s'); } },
      { get title() { return i18n.t('ak_docsdata_for_wastewater_disposal_and_water_supp'); } },
      { get title() { return i18n.t('ak_docsdata_electrical_equipment_testing_metrology'); } },
      { get title() { return i18n.t('ak_docsdata_for_waste_removal'); } },
    ],
  },
];

// ── 3. Звітність ────────────────────────────────────────────────────────────
export interface ReportItem {
  title: string;
  form?: string;
  /** Періодичність — головний орієнтир: за нею планують роботу. */
  period?: string;
}
export interface ReportGroup {
  Icon: LucideIcon;
  label: string;
  scope: DocScope;
  hint?: string;
  items: ReportItem[];
}

export const REPORTS: ReportGroup[] = [
  {
    get label() { return i18n.t('ak_docsdata_neurc'); }, Icon: Gavel, scope: 'company',
    get hint() { return i18n.t('ak_docsdata_monitoring_reporting'); },
    items: [
      { get title() { return i18n.t('ak_docsdata_report_on_general_characteristics_of_t'); }, get form() { return i18n.t('ak_docsdata_no_1_neurc_monitoring_generation'); }, get period() { return i18n.t('ak_docsdata_annual'); } },
      { get title() { return i18n.t('ak_docsdata_report_on_net_income'); }, get form() { return i18n.t('ak_docsdata_no_1_neurc_income'); }, get period() { return i18n.t('ak_docsdata_quarterly'); } },
      { get title() { return i18n.t('ak_docsdata_report_on_electricity_purchase_and_sal'); }, get form() { return i18n.t('ak_docsdata_no_2_neurc_monitoring_generation'); }, get period() { return i18n.t('ak_docsdata_monthly'); } },
      { get title() { return i18n.t('ak_docsdata_report_on_electricity_generation_activ'); }, get form() { return i18n.t('ak_docsdata_no_4_neurc_monitoring_generation'); }, get period() { return i18n.t('ak_docsdata_monthly'); } },
      { get title() { return i18n.t('ak_docsdata_report_on_payment_of_the_regulation_fe'); }, get form() { return i18n.t('ak_docsdata_no_20_neurc'); }, get period() { return i18n.t('ak_docsdata_quarterly'); } },
      { get title() { return i18n.t('ak_docsdata_financial_statements_balance_sheet_sta'); }, get form() { return i18n.t('ak_docsdata_neurc_qes'); }, get period() { return i18n.t('ak_docsdata_quarterly_annual'); } },
      { get title() { return i18n.t('ak_docsdata_report_on_business_and_trading_transac'); }, get form() { return i18n.t('ak_docsdata_appendix_no_2'); }, get period() { return i18n.t('ak_docsdata_on_event'); } },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_npc_ukrenergo'); }, Icon: Waypoints, scope: 'station',
    get hint() { return i18n.t('ak_docsdata_industry_reporting'); },
    items: [
      { get title() { return i18n.t('ak_docsdata_electricity_balance_report'); }, get form() { return i18n.t('ak_docsdata_form_no_37_energo'); }, get period() { return i18n.t('ak_docsdata_monthly'); } },
      { get title() { return i18n.t('ak_docsdata_ee_heat_balance_tech_econ'); }, get form() { return i18n.t('ak_docsdata_layout_8111'); }, get period() { return i18n.t('ak_docsdata_monthly'); } },
      { get title() { return i18n.t('ak_docsdata_ee_heat_balance_tech_econ'); }, get form() { return i18n.t('ak_docsdata_layout_8112'); }, get period() { return i18n.t('ak_docsdata_monthly'); } },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_state_statistics_service'); }, Icon: BarChart3, scope: 'company',
    get hint() { return i18n.t('ak_docsdata_statistical_reporting'); },
    items: [
      { get title() { return i18n.t('ak_docsdata_report_on_energy_supply_and_use'); }, get form() { return i18n.t('ak_docsdata_no_11_mtp'); }, get period() { return i18n.t('ak_docsdata_annual'); } },
      { get title() { return i18n.t('ak_docsdata_report_on_fuel_use_and_stocks'); }, get form() { return i18n.t('ak_docsdata_no_4_mtp'); }, get period() { return i18n.t('ak_docsdata_monthly_annual'); } },
      { get title() { return i18n.t('ak_docsdata_labour_report'); }, get form() { return i18n.t('ak_docsdata_form_1_pv'); }, get period() { return i18n.t('ak_docsdata_monthly_quarterly'); } },
      { get title() { return i18n.t('ak_docsdata_wage_arrears_report'); }, get form() { return i18n.t('ak_docsdata_form_no_3'); }, get period() { return i18n.t('ak_docsdata_monthly'); } },
      { get title() { return i18n.t('ak_docsdata_industry_short_term_stats'); }, get form() { return i18n.t('ak_docsdata_form_1_pe'); }, get period() { return i18n.t('ak_docsdata_monthly'); } },
    ],
  },
  {
    get label() { return i18n.t('ak_docsdata_tax_service'); }, Icon: Receipt, scope: 'company',
    get hint() { return i18n.t('ak_docsdata_tax_reporting'); },
    items: [
      { get title() { return i18n.t('ak_docsdata_financial_statements'); }, get form() { return i18n.t('ak_docsdata_form_1ms_2ms'); }, get period() { return i18n.t('ak_docsdata_quarterly_annual'); } },
      { get title() { return i18n.t('ak_docsdata_vat_return'); }, get period() { return i18n.t('ak_docsdata_monthly'); } },
      { get title() { return i18n.t('ak_docsdata_excise_return'); }, get period() { return i18n.t('ak_docsdata_monthly'); } },
      { get title() { return i18n.t('ak_docsdata_profit_tax_return'); }, get period() { return i18n.t('ak_docsdata_annual'); } },
      { get title() { return i18n.t('ak_docsdata_land_fee_return'); }, get period() { return i18n.t('ak_docsdata_annual'); } },
    ],
  },
];

// ── 4. Технічна документація ────────────────────────────────────────────────
/**
 * Файли технічної документації — окремою мапою, а не полями списку.
 *
 * TECHNICAL лишається простим переліком назв (його правлять найчастіше), а
 * прив'язка файлів живе поряд: ключ — точна назва позиції. Поки що є лише
 * перелік ТКО по Лучках.
 */
/**
 * Технічна документація — той самий DocLink, що й в інших вкладках.
 *
 * Був простим переліком назв, а файли жили в окремій мапі поруч. Через це
 * додати сюди документ означало правити ДВА місця, тоді як у договорах —
 * одне. Тепер форма запису однакова скрізь.
 */
export const TECHNICAL: DocLink[] = [
  {
    get title() { return i18n.t('ak_docsdata_neurc_license'); },
    // Реквізит узятий із назви файла, наданої замовником: «Постанова НКРЕКП
    // 829 від 02.06.2026». Текстовий шар документа дає лише «2020», а
    // метадані — 21.07.2025; обидва суперечать назві, тож джерелом вважаємо
    // те, як документ назвав власник.
    get meta() { return i18n.t('ak_docsdata_neurc_resolution_of_02_06_2026_no_829'); },
    date: '2026-06-02',
    file: '/docs/luchky-license-nkrekp-829.pdf',
    station: 'лучки',
  },
  { get title() { return i18n.t('ak_docsdata_technical_conditions_for_non_standard'); } },
  { get title() { return i18n.t('ak_docsdata_npc_ukrenergo_notification_to_the_bala'); } },
  { get title() { return i18n.t('ak_docsdata_technical_passport'); } },
  { get title() { return i18n.t('ak_docsdata_act_of_demarcation_of_balance_sheet_ow'); } },
  { get title() { return i18n.t('ak_docsdata_main_single_line_diagram_of_electrical'); } },
  // ОКРЕМА позиція, а не файл до схеми вище. Надана схема — про розташування
  // приладів розрахункового обліку, тобто інший документ: головна однолінійна
  // показує з’єднання станції, ця — де стоять лічильники. Підвести файл під
  // наявну назву означало б назвати документ не тим, чим він є.
  {
    get title() { return i18n.t('ak_docsdata_single_line_diagram_of_the_metering_de'); },
    file: '/docs/luchky-metering-single-line.pdf',
    station: 'лучки',
  },
  { get title() { return i18n.t('ak_docsdata_master_plan_with_equipment_list'); } },
  { get title() { return i18n.t('ak_docsdata_askoe_and_communication_channel_diagra'); } },
  { get title() { return i18n.t('ak_docsdata_tko_list'); }, file: '/docs/luchky-tko-list.xlsx', station: 'лучки' },
  { get title() { return i18n.t('ak_docsdata_meter_and_instrument_transformer_passp'); } },
  { get title() { return i18n.t('ak_docsdata_relay_protection_settings_card_approve'); } },
  { get title() { return i18n.t('ak_docsdata_main_equipment_specification_with_seri'); } },
  // Акт підключення й установчі документи — перелік v2.2 (22.10.2026), «Ні (потрібно додати)».
  { get title() { return i18n.t('ak_docsdata_helios_ems_connection_act'); } },
  { get title() { return i18n.t('ak_docsdata_edr_extract'); }, date: '2026-10-15', file: '/docs/luchky-edr-extract-a860fb3439.pdf', station: 'лучки' },
  { get title() { return i18n.t('ak_docsdata_charter_signatory_authority'); }, date: '2024-07-31', file: '/docs/luchky-statut-a860fb3439.pdf', station: 'лучки' },
  { get title() { return i18n.t('ak_docsdata_vat_payer_certificate'); }, date: '2024-12-09', file: '/docs/luchky-vat-extract-a860fb3439.pdf', station: 'лучки' },
];
